import { useRef } from 'react';

import { MAX_QUIZ_COUNT } from '@/views/build/constant';
import { useBuildInfoContext } from '@/views/build/context/BuildInfoProvider';
import { useGetCategoryFormValue } from '@/views/build/features/category-form';
import { type QuizBankProblem } from '@/views/build/features/quiz-bank';
import { useBuildFormContext } from '@/views/build/form/schema';
import { findSubjectName } from '@/views/build/util/findSubjectName';
import { getAddedQuizBankItemIds } from '@/views/build/util/getAddedQuizBankItemIds';
import { getBuildType } from '@/views/build/util/getBuildType';
import { toBuildQuizListItems } from '@/views/build/util/toBuildQuizListItems';

import { useSyncQuizTitleWithCategory } from '../../build-quiz-title';
import { quizSetupModal } from '../index';

export const useShowQuizSetupModal = () => {
  const {
    action,
    mapTemplates,
    schoolInfoTree,
    subjectTree,
    publishers,
    userCountryCode,
    quizBankExposedCategoryPrefixes,
  } = useBuildInfoContext();
  const { setValue, getValues } = useBuildFormContext();

  // 카테고리 정보 form hook 사용
  const { getCategoryFormValue, updateCategoryFormValue } =
    useGetCategoryFormValue();
  const syncQuizTitleWithCategory = useSyncQuizTitleWithCategory({
    schoolInfoTree,
    subjectTree,
    publishers,
    userCountryCode,
  });

  const isOpened = useRef(false);

  const open = async () => {
    if (isOpened.current) return;
    isOpened.current = true;

    try {
      // 완료로 닫혔는지(true) 그냥 닫혔는지(false) — 초안 버튼 노출 판단에 쓴다.
      return await quizSetupModal.openAsync({
        mapTemplates,
        schoolInfoTree,
        subjectTree,
        publishers,
        userCountryCode,
        quizBankExposedCategoryPrefixes,
        buildType: getBuildType(action),
        initialCategoryInfoValues: getCategoryFormValue(schoolInfoTree),
        quizBankRemainingSlots: Math.max(
          0,
          MAX_QUIZ_COUNT - getValues('quizList').length,
        ),
        quizBankAddedProblemIds: getAddedQuizBankItemIds(getValues('quizList')),
        onMapComplete: (selectedMapTemplateId, mapTemplateType) => {
          setValue('templateId', selectedMapTemplateId, {
            shouldDirty: true,
          });
          setValue('templateType', mapTemplateType, {
            shouldDirty: true,
          });
        },
        onCategoryInfoComplete: values => {
          updateCategoryFormValue(values);
          syncQuizTitleWithCategory(values);
        },
        onQuizBankComplete: (problems: QuizBankProblem[]) => {
          if (problems.length === 0) return;

          // BuildPageInitializer 는 QuizListProvider 바깥이라 useQuizList 대신 setValue 로 이어 붙인다.
          const subjectTreeId = getValues('subject') ?? '';
          const subject = findSubjectName(subjectTree, subjectTreeId) ?? '';

          setValue(
            'quizList',
            [
              ...getValues('quizList'),
              ...toBuildQuizListItems(problems, { subject }),
            ],
            { shouldDirty: true },
          );
        },
      });
    } catch (error) {
      console.error('Quiz setup modal error:', error);
      return false;
    } finally {
      isOpened.current = false;
    }
  };

  return { open };
};
